import React, { useState, useEffect, useRef } from 'react';
import { getOperationLogs, getLogStatistics, getDatabaseInfo, backupDatabase, restoreDatabase, cleanupLogs, exportLogsCSV, getSystemSettings, updateSystemSettings, updateUser, getBackups, getBackup, deleteBackup } from '../api';

function SystemManagement() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [dbInfo, setDbInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('user');
  const [settings, setSettings] = useState({});
  const [userInfo, setUserInfo] = useState(null);
  const [userForm, setUserForm] = useState({ nickname: '', phone: '', newPassword: '', confirmPassword: '' });
  const [logFilter, setLogFilter] = useState({ userId: '', username: '', startDate: '', endDate: '' });
  const [backups, setBackups] = useState([]);

  useEffect(() => {
    fetchData();
    fetchBackups();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [logsRes, statsRes, dbRes, settingsRes] = await Promise.all([
        getOperationLogs({ pageSize: 50 }),
        getLogStatistics(),
        getDatabaseInfo(),
        getSystemSettings()
      ]);
      if (logsRes.data.success) setLogs(logsRes.data.data);
      if (statsRes.data.success) setStats(statsRes.data.data);
      if (dbRes.data.success) setDbInfo(dbRes.data.data);
      if (settingsRes.data.success) setSettings(settingsRes.data.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchBackups = async () => {
    try {
      const res = await getBackups();
      if (res.data.success) setBackups(res.data.data);
    } catch (e) { console.error(e); }
  };

  const handleUpdateUser = async () => {
    try {
      if (userForm.newPassword && userForm.newPassword !== userForm.confirmPassword) {
        alert('两次输入的密码不一致');
        return;
      }
      const data = { nickname: userForm.nickname, phone: userForm.phone };
      if (userForm.newPassword) data.password = userForm.newPassword;
      const res = await updateUser(userInfo.id, data);
      if (res.data.success) {
        alert('保存成功');
        setUserForm({...userForm, newPassword: '', confirmPassword: ''});
        const savedUser = JSON.parse(sessionStorage.getItem('health_user') || '{}');
        savedUser.nickname = userForm.nickname;
        savedUser.phone = userForm.phone;
        sessionStorage.setItem('health_user', JSON.stringify(savedUser));
      } else {
        alert(res.data.message || '保存失败');
      }
    } catch (e) { alert('保存失败'); }
  };

  const handleBackup = async () => {
    try {
      await backupDatabase();
      alert('备份成功！');
      fetchBackups();
    } catch (e) { alert('备份失败：' + e.message); }
  };

  const handleDownloadBackup = async (filename) => {
    try {
      const res = await getBackup(filename);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
    } catch (e) { alert('下载失败'); }
  };

  const handleDeleteBackup = async (filename) => {
    if (!window.confirm('确定要删除这个备份吗？')) return;
    try {
      await deleteBackup(filename);
      alert('删除成功');
      fetchBackups();
    } catch (e) { alert('删除失败'); }
  };

  const fileInputRef = useRef(null);

  const handleRestore = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!window.confirm('确定要恢复数据库吗？恢复后当前数据将被覆盖，此操作不可恢复！')) return;
    try {
      await restoreDatabase(file);
      alert('恢复成功！请重新登录系统。');
    } catch (e) { alert('恢复失败：' + e.message); }
    e.target.value = '';
  };

  const handleCleanupLogs = async () => {
    const days = prompt('请输入要保留的天数（超过此天数的日志将被删除）：', '30');
    if (!days || isNaN(days)) return;
    if (!window.confirm(`确定要删除${days}天前的所有日志吗？此操作不可恢复！`)) return;
    try {
      await cleanupLogs(parseInt(days));
      alert('日志清理完成！');
      fetchData();
    } catch (e) { alert('清理失败：' + e.message); }
  };

  const handleDeleteLogsByFilter = async () => {
    const { userId, username, startDate, endDate } = logFilter;
    if (!userId && !username && !startDate && !endDate) {
      alert('请设置筛选条件');
      return;
    }
    if (!window.confirm('确定要删除符合筛选条件的日志吗？此操作不可恢复！')) return;
    try {
      await cleanupLogs(null, logFilter);
      alert('日志删除完成！');
      fetchData();
    } catch (e) { alert('删除失败：' + e.message); }
  };

  const handleExportLogsFiltered = async () => {
    try {
      const res = await exportLogsCSV(logFilter);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `operation_logs_${new Date().toISOString().slice(0,10)}.csv`;
      link.click();
      alert('导出成功！');
    } catch (e) { alert('导出失败：' + e.message); }
  };

  const handleToggleAutoCleanup = async (enabled) => {
    try {
      await updateSystemSettings('auto_cleanup_logs', enabled ? 'true' : 'false');
      setSettings(prev => ({ ...prev, auto_cleanup_logs: enabled ? 'true' : 'false' }));
      alert(enabled ? '自动清理已开启' : '自动清理已关闭');
    } catch (e) { alert('设置失败：' + e.message); }
  };

  const handleChangeCleanupDays = async () => {
    const days = prompt('请输入自动清理保留天数：', settings.auto_cleanup_days || '30');
    if (!days || isNaN(days) || days < 1) return;
    try {
      await updateSystemSettings('auto_cleanup_days', days);
      setSettings(prev => ({ ...prev, auto_cleanup_days: days }));
      alert('清理天数已设置为 ' + days + ' 天');
    } catch (e) { alert('设置失败：' + e.message); }
  };

  const getActionBadge = (action) => {
    const map = { 'login': 'badge-blue', 'create': 'badge-green', 'update': 'badge-yellow', 'delete': 'badge-red' };
    return <span className={`status-badge ${map[action] || 'badge-gray'}`}>{action}</span>;
  };

  if (loading) return <div className="loading-text">加载中...</div>;

  const savedUser = JSON.parse(sessionStorage.getItem('health_user') || '{}');
  const curUserInfo = userInfo || savedUser;

  return (
    <div>
      <div className="content-card">
        <div className="section-title" style={{ display: 'flex', gap: '16px', cursor: 'pointer' }}>
          <span style={{ fontWeight: tab === 'user' ? 'bold' : 'normal', color: tab === 'user' ? '#1890ff' : '#666' }}
            onClick={() => setTab('user')}>用户信息</span>
          <span style={{ fontWeight: tab === 'setting' ? 'bold' : 'normal', color: tab === 'setting' ? '#1890ff' : '#666' }}
            onClick={() => setTab('setting')}>系统设置</span>
          <span style={{ fontWeight: tab === 'logs' ? 'bold' : 'normal', color: tab === 'logs' ? '#1890ff' : '#666' }}
            onClick={() => setTab('logs')}>操作日志</span>
          <span style={{ fontWeight: tab === 'data' ? 'bold' : 'normal', color: tab === 'data' ? '#1890ff' : '#666' }}
            onClick={() => setTab('data')}>数据维护</span>
        </div>
      </div>

      {tab === 'user' && (
        <div className="content-card">
          <div className="section-title">用户信息</div>
          <div style={{ maxWidth: '400px' }}>
            <div className="form-group">
              <label className="form-label">用户名</label>
              <input className="form-input" value={curUserInfo?.username || ''} disabled />
            </div>
            <div className="form-group">
              <label className="form-label">昵称</label>
              <input className="form-input" value={userForm.nickname}
                onChange={e => setUserForm({...userForm, nickname: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">手机号</label>
              <input className="form-input" value={userForm.phone}
                onChange={e => setUserForm({...userForm, phone: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">新密码（留空则不修改）</label>
              <input className="form-input" type="password" value={userForm.newPassword}
                onChange={e => setUserForm({...userForm, newPassword: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">确认密码</label>
              <input className="form-input" type="password" value={userForm.confirmPassword}
                onChange={e => setUserForm({...userForm, confirmPassword: e.target.value})} />
            </div>
            <button className="action-btn btn-blue" onClick={handleUpdateUser}>保存</button>
          </div>
        </div>
      )}

      {tab === 'setting' && (
        <div className="content-card">
          <div className="section-title">系统设置</div>
          <div style={{ maxWidth: '400px' }}>
            <div className="form-group">
              <label className="form-label">系统名称</label>
              <input className="form-input" value={settings.system_name || ''}
                onChange={e => setSettings({...settings, system_name: e.target.value})} />
            </div>
            <button className="action-btn btn-blue" onClick={async () => {
              try {
                await updateSystemSettings('system_name', settings.system_name);
                // 保存到sessionStorage并刷新页面
                sessionStorage.setItem('system_name', settings.system_name || '');
                window.location.reload();
              } catch (e) { alert('保存失败'); }
            }}>保存</button>
          </div>
        </div>
      )}

      {tab === 'logs' && (
        <div className="content-card">
          <div style={{ marginBottom: '16px', padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>筛选条件</div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <input className="form-input" style={{ width: '120px' }} placeholder="用户ID"
                value={logFilter.userId} onChange={e => setLogFilter({...logFilter, userId: e.target.value})} />
              <input className="form-input" style={{ width: '120px' }} placeholder="用户名"
                value={logFilter.username} onChange={e => setLogFilter({...logFilter, username: e.target.value})} />
              <input className="form-input" style={{ width: '140px' }} type="date" placeholder="开始日期"
                value={logFilter.startDate} onChange={e => setLogFilter({...logFilter, startDate: e.target.value})} />
              <span>-</span>
              <input className="form-input" style={{ width: '140px' }} type="date" placeholder="结束日期"
                value={logFilter.endDate} onChange={e => setLogFilter({...logFilter, endDate: e.target.value})} />
              <button className="action-btn btn-blue" onClick={fetchData}>查询</button>
              <button className="action-btn btn-gray" onClick={() => setLogFilter({ userId: '', username: '', startDate: '', endDate: '' })}>重置</button>
            </div>
          </div>

          <div style={{ marginBottom: '12px', display: 'flex', gap: '8px' }}>
            <button className="action-btn btn-green" onClick={handleExportLogsFiltered}>导出选中日志</button>
            <button className="action-btn btn-red" onClick={handleDeleteLogsByFilter}>删除选中日志</button>
            <button className="action-btn btn-orange" onClick={handleCleanupLogs}>清理旧日志</button>
          </div>

          <div className="stats-cards" style={{ marginBottom: '16px' }}>
            <div className="stats-row">
              <div className="stat-box"><div className="stat-label">登录次数</div><div className="stat-value">{stats.actionStats?.login || 0}</div></div>
              <div className="stat-box"><div className="stat-label">创建操作</div><div className="stat-value green">{stats.actionStats?.create || 0}</div></div>
              <div className="stat-box"><div className="stat-label">更新操作</div><div className="stat-value orange">{stats.actionStats?.update || 0}</div></div>
              <div className="stat-box"><div className="stat-label">删除操作</div><div className="stat-value red">{stats.actionStats?.delete || 0}</div></div>
            </div>
          </div>

          <table className="basic-table">
            <thead><tr><th className="no-cell">序号</th><th>操作人</th><th>操作类型</th><th>操作对象</th><th>详情</th><th>时间</th></tr></thead>
            <tbody>
              {logs.length > 0 ? logs.map((log, i) => (
                <tr key={log.id}>
                  <td className="no-cell">{i + 1}</td>
                  <td>{log.username}</td>
                  <td>{getActionBadge(log.action)}</td>
                  <td>{log.target_type || '-'}</td>
                  <td>{log.details || '-'}</td>
                  <td>{log.created_at ? log.created_at.slice(0, 19) : '-'}</td>
                </tr>
              )) : (<tr><td colSpan="6" style={{ textAlign: 'center', color: '#999' }}>暂无日志</td></tr>)}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'data' && (
        <div className="content-card">
          <div className="section-title">数据备份与恢复</div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontWeight: 'bold' }}>服务器备份列表</span>
              <button className="action-btn btn-green" onClick={handleBackup}>新建备份</button>
            </div>
            {backups.length > 0 ? (
              <table className="basic-table">
                <thead><tr><th>文件名</th><th>大小</th><th>创建时间</th><th>操作</th></tr></thead>
                <tbody>
                  {backups.map(b => (
                    <tr key={b.filename}>
                      <td>{b.filename}</td>
                      <td>{(b.size / 1024).toFixed(1)} KB</td>
                      <td>{new Date(b.createdAt).toLocaleString()}</td>
                      <td>
                        <button className="action-btn btn-blue" style={{ padding: '4px 12px', marginRight: '8px' }}
                          onClick={() => handleDownloadBackup(b.filename)}>下载</button>
                        <button className="action-btn btn-red" style={{ padding: '4px 12px' }}
                          onClick={() => handleDeleteBackup(b.filename)}>删除</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ color: '#999', padding: '20px', textAlign: 'center' }}>暂无备份</div>
            )}
          </div>

          <div style={{ marginTop: '20px', padding: '16px', background: '#f5f5f5', borderRadius: '4px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '12px' }}>从本地恢复数据库</div>
            <input type="file" ref={fileInputRef} accept=".db,.sql" style={{ display: 'none' }} onChange={handleRestore} />
            <button className="action-btn btn-blue" onClick={() => fileInputRef.current.click()}>选择备份文件</button>
            <div style={{ marginTop: '8px', color: '#999', fontSize: '12px' }}>支持 .db 和 .sql 格式，恢复后将覆盖当前数据</div>
          </div>

          <div className="section-title" style={{ marginTop: '32px' }}>数据库统计</div>
          <table className="basic-table">
            <thead><tr><th>表名</th><th>记录数</th></tr></thead>
            <tbody>
              {Object.entries(dbInfo.tableCounts || {}).map(([table, count]) => (
                <tr key={table}><td>{table}</td><td>{count}</td></tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: '16px', color: '#666', fontSize: '13px' }}>
            数据库大小：{Math.round((dbInfo.dbSize || 0) / 1024)} KB<br/>
            最后修改：{dbInfo.lastModified ? new Date(dbInfo.lastModified).toLocaleString() : '-'}
          </div>
        </div>
      )}
    </div>
  );
}

export default SystemManagement;